import React, { useCallback, useEffect, useState } from 'react';
import { ArrowRight, Map as MapIcon, Route } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../services/apiClient';
import { getLanguage, t } from '../i18n';
import './StudyPathPage.css';

function StudyPathPage() {
  const navigate = useNavigate();
  const language = getLanguage();
  const [path, setPath] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const copy = language === 'ko' ? {
    title: '나의 학습 경로', intro: '가져온 공식 노트의 다음 순서에서 학습할 노트를 추천해요.',
    next: '다음 추천 노트', course: '전체 코스 보기', map: '전체 라이브러리 학습 지도',
    level: '난이도 {level}', imported: '가져옴', mastered: '마스터', choose: '노트 선택',
    noNext: '지금 추천할 노트가 없습니다.', recommendations: '이번 추천 목록', count: '{count}개 노트',
  } : {
    title: 'My study path', intro: 'Recommendations follow the notes after your imported official notes.',
    next: 'Next recommended note', course: 'View full course', map: 'Full library learning map',
    level: 'Level {level}', imported: 'Imported', mastered: 'Mastered', choose: 'Choose note',
    noNext: 'There are no notes to recommend right now.', recommendations: 'Recommended notes', count: '{count} notes',
  };
  const load = useCallback(() => {
    setLoading(true); setError('');
    apiClient.getStudyPath(language).then(setPath).catch((e) => setError(e.message || 'Could not load study path.')).finally(() => setLoading(false));
  }, [language]);
  useEffect(() => { load(); }, [load]);
  const open = (note) => navigate(`/library/${encodeURIComponent(note.id)}`, { state: { libraryReturnTo: '/study-path' } });
  const levelLabel = (level) => t('library.level', undefined, { level });
  if (loading) return <main className="study-path"><p role="status">{t('library.loading')}</p></main>;
  return <main className="study-path">
    <header className="study-path-header"><div><span className="study-path-eyebrow"><Route size={16} />{copy.next}</span><h1>{copy.title}</h1><p>{copy.intro}</p></div>
      <button type="button" className="study-path-map-link" onClick={() => document.getElementById('study-map')?.scrollIntoView({ behavior: 'smooth' })}><MapIcon size={18} />{copy.course}</button>
    </header>
    {error && <p className="study-path-error" role="alert">{error}</p>}
    <section className="study-path-next"><h2>{copy.next}</h2>{path?.next ? <button className="study-path-feature" onClick={() => open(path.next)}>
      <span className="study-path-feature-meta">{levelLabel(path.next.difficulty)} <span>#{path.next.sortOrder}</span></span><strong>{path.next.title}</strong>
      <span className="study-path-feature-bottom">{path.next.imported ? copy.imported : copy.choose}<ArrowRight size={18} /></span>
    </button> : <p>{copy.noNext}</p>}
      <h3>{copy.recommendations}</h3><div className="study-path-picks">{(path?.recommendations || []).map((item) => <button key={item.id} onClick={() => open(item)}>
        <span>{levelLabel(item.difficulty)} · #{item.sortOrder}</span><strong>{item.title}</strong><small>{item.mastered ? copy.mastered : item.imported ? copy.imported : copy.choose}</small>
      </button>)}</div>
    </section>
    <section className="study-path-map" id="study-map"><header><div><h2>{copy.map}</h2><p>{copy.count.replace('{count}', path?.total || 0)}</p></div></header>
      <ol>{(path?.items || []).map((item) => <li key={item.id} className={item.mastered ? 'is-mastered' : item.imported ? 'is-imported' : ''}>
        <span className="study-path-order">{item.sortOrder}</span><button onClick={() => open(item)}><span>{levelLabel(item.difficulty)}</span><strong>{item.title}</strong></button>
        <span className="study-path-state">{item.mastered ? copy.mastered : item.imported ? copy.imported : ''}</span>
      </li>)}</ol>
    </section>
  </main>;
}

export default StudyPathPage;
